import { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import {
    BarChart3,
    TrendingUp,
    Clock,
    AlertTriangle,
    CheckCircle,
    FolderOpen,
    ClipboardList
} from 'lucide-react';

export default function TicketDashboard() {
    const { tickets, departments } = useApp();

    // Calculate metrics
    const metrics = useMemo(() => {
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in_progress');
        const closedTickets = tickets.filter(t => t.status === 'closed' || t.status === 'resolved');
        const overdueTickets = tickets.filter(t =>
            t.due_date && new Date(t.due_date) < now &&
            t.status !== 'closed' && t.status !== 'resolved'
        );
        const createdThisWeek = tickets.filter(t => new Date(t.created_at) >= oneWeekAgo);

        // Average resolution time (days)
        const resolvedTickets = closedTickets.filter(t => t.activity_log?.some(a => a.action === 'status_changed' && a.details?.to === 'closed'));
        let avgResolutionDays = 0;
        if (resolvedTickets.length > 0) {
            const totalDays = resolvedTickets.reduce((sum, t) => {
                const created = new Date(t.created_at);
                const resolved = t.activity_log?.find(a => a.action === 'status_changed' && a.details?.to === 'closed');
                if (resolved) {
                    return sum + (new Date(resolved.timestamp).getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
                }
                return sum;
            }, 0);
            avgResolutionDays = totalDays / resolvedTickets.length;
        }

        // Status counts
        const statusCounts = {
            open: tickets.filter(t => t.status === 'open').length,
            in_progress: tickets.filter(t => t.status === 'in_progress').length,
            resolved: tickets.filter(t => t.status === 'resolved').length,
            closed: tickets.filter(t => t.status === 'closed').length
        };

        // Priority counts
        const priorityCounts = {
            urgent: tickets.filter(t => t.priority === 'urgent').length,
            high: tickets.filter(t => t.priority === 'high').length,
            medium: tickets.filter(t => t.priority === 'medium').length,
            low: tickets.filter(t => t.priority === 'low').length
        };

        // By department
        const byDepartment = departments.map(dept => ({
            name: dept.name,
            color: dept.color,
            count: tickets.filter(t => t.department_id === dept.id).length
        })).filter(d => d.count > 0);

        // Weekly trend (last 4 weeks)
        const weeklyTrend: { week: string; created: number; closed: number }[] = [];
        for (let i = 3; i >= 0; i--) {
            const weekStart = new Date(now.getTime() - (i + 1) * 7 * 24 * 60 * 60 * 1000);
            const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
            weeklyTrend.push({
                week: `W${4 - i}`,
                created: tickets.filter(t => {
                    const d = new Date(t.created_at);
                    return d >= weekStart && d < weekEnd;
                }).length,
                closed: closedTickets.filter(t => {
                    const closedEntry = t.activity_log?.find(a => a.action === 'status_changed' && a.details?.to === 'closed');
                    if (closedEntry) {
                        const d = new Date(closedEntry.timestamp);
                        return d >= weekStart && d < weekEnd;
                    }
                    return false;
                }).length
            });
        }

        return {
            total: tickets.length,
            open: openTickets.length,
            overdue: overdueTickets.length,
            createdThisWeek: createdThisWeek.length,
            avgResolutionDays: Math.round(avgResolutionDays * 10) / 10,
            statusCounts,
            priorityCounts,
            byDepartment,
            weeklyTrend
        };
    }, [tickets, departments]);

    const MetricCard = ({ icon: Icon, label, value, color, subtext }: {
        icon: any;
        label: string;
        value: number | string;
        color: string;
        subtext?: string;
    }) => (
        <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-lg)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-sm)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-md)',
                    background: `${color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Icon size={20} color={color} />
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>{label}</span>
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
            {subtext && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{subtext}</div>}
        </div>
    );

    const SimpleBarChart = ({ data, labelKey, valueKey, colorKey }: {
        data: any[];
        labelKey: string;
        valueKey: string;
        colorKey?: string;
    }) => {
        const maxValue = Math.max(...data.map(d => d[valueKey]), 1);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                {data.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                        <span style={{
                            width: 80,
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--text-muted)',
                            textTransform: 'capitalize'
                        }}>
                            {item[labelKey].replace('_', ' ')}
                        </span>
                        <div style={{ flex: 1, height: 20, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                            <div style={{
                                width: `${(item[valueKey] / maxValue) * 100}%`,
                                height: '100%',
                                background: colorKey ? item[colorKey] : 'var(--accent-primary)',
                                borderRadius: 'var(--radius-sm)',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                        <span style={{ width: 30, fontSize: 'var(--font-size-sm)', textAlign: 'right' }}>{item[valueKey]}</span>
                    </div>
                ))}
            </div>
        );
    };

    const statusData = [
        { status: 'open', count: metrics.statusCounts.open, color: 'var(--accent-info)' },
        { status: 'in_progress', count: metrics.statusCounts.in_progress, color: 'var(--color-warning)' },
        { status: 'resolved', count: metrics.statusCounts.resolved, color: 'var(--accent-success)' },
        { status: 'closed', count: metrics.statusCounts.closed, color: 'var(--text-muted)' }
    ];

    const priorityData = [
        { priority: 'urgent', count: metrics.priorityCounts.urgent, color: 'var(--accent-danger)' },
        { priority: 'high', count: metrics.priorityCounts.high, color: 'var(--color-warning)' },
        { priority: 'medium', count: metrics.priorityCounts.medium, color: 'var(--accent-info)' },
        { priority: 'low', count: metrics.priorityCounts.low, color: 'var(--text-muted)' }
    ];

    return (
        <div style={{
            padding: 'var(--spacing-md)',
            height: '100%',
            width: '100%',
            flex: 1,
            overflow: 'auto'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)',
                marginBottom: 'var(--spacing-lg)'
            }}>
                <BarChart3 size={24} color="var(--accent-primary)" />
                <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
                    Ticket Dashboard
                </h2>
            </div>

            {/* Metric Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-xl)'
            }}>
                <MetricCard
                    icon={ClipboardList}
                    label="Open Tickets"
                    value={metrics.open}
                    color="var(--accent-info)"
                    subtext={`${metrics.total} total`}
                />
                <MetricCard
                    icon={AlertTriangle}
                    label="Overdue"
                    value={metrics.overdue}
                    color="var(--accent-danger)"
                />
                <MetricCard
                    icon={Clock}
                    label="Avg Resolution"
                    value={`${metrics.avgResolutionDays}d`}
                    color="var(--accent-secondary)"
                />
                <MetricCard
                    icon={TrendingUp}
                    label="Created This Week"
                    value={metrics.createdThisWeek}
                    color="var(--accent-success)"
                />
            </div>

            {/* Charts Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 'var(--spacing-lg)'
            }}>
                {/* Status Chart */}
                <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)'
                }}>
                    <h3 style={{
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        marginBottom: 'var(--spacing-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <CheckCircle size={18} />
                        By Status
                    </h3>
                    <SimpleBarChart data={statusData} labelKey="status" valueKey="count" colorKey="color" />
                </div>

                {/* Priority Chart */}
                <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)'
                }}>
                    <h3 style={{
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        marginBottom: 'var(--spacing-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <AlertTriangle size={18} />
                        By Priority
                    </h3>
                    <SimpleBarChart data={priorityData} labelKey="priority" valueKey="count" colorKey="color" />
                </div>

                {/* Department Chart */}
                {metrics.byDepartment.length > 0 && (
                    <div style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-lg)'
                    }}>
                        <h3 style={{
                            fontSize: 'var(--font-size-md)',
                            fontWeight: 600,
                            marginBottom: 'var(--spacing-md)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <FolderOpen size={18} />
                            By Department
                        </h3>
                        <SimpleBarChart
                            data={metrics.byDepartment}
                            labelKey="name"
                            valueKey="count"
                            colorKey="color"
                        />
                    </div>
                )}

                {/* Weekly Trend */}
                <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)'
                }}>
                    <h3 style={{
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        marginBottom: 'var(--spacing-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <TrendingUp size={18} />
                        Weekly Trend
                    </h3>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)', height: 120, alignItems: 'flex-end' }}>
                        {metrics.weeklyTrend.map((week, idx) => (
                            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 80 }}>
                                    <div style={{
                                        width: 16,
                                        height: `${(week.created / Math.max(...metrics.weeklyTrend.map(w => Math.max(w.created, w.closed)), 1)) * 80}px`,
                                        background: 'var(--accent-primary)',
                                        borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0'
                                    }} title={`Created: ${week.created}`} />
                                    <div style={{
                                        width: 16,
                                        height: `${(week.closed / Math.max(...metrics.weeklyTrend.map(w => Math.max(w.created, w.closed)), 1)) * 80}px`,
                                        background: 'var(--accent-success)',
                                        borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0'
                                    }} title={`Closed: ${week.closed}`} />
                                </div>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{week.week}</span>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)' }}>
                            <div style={{ width: 10, height: 10, background: 'var(--accent-primary)', borderRadius: 2 }} />
                            Created
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)' }}>
                            <div style={{ width: 10, height: 10, background: 'var(--accent-success)', borderRadius: 2 }} />
                            Closed
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
